import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Bell as BellIcon, Building2, CalendarClock, CheckCheck, FileCheck, MessageCircle, Newspaper, Share2, SquareKanban, Users } from 'lucide-react';
import { Avatar, CountBadge, Popover, Spinner, Tip } from '@vatman/ui';
import { ApiError } from '../lib/api';
import { errorText } from '../i18n';
import { relativeTime } from '../lib/time';
import { todayISO } from '../engine/cards';
import { track } from '../lib/track';
import {
  listKey,
  loadMore,
  markAllRead,
  markRead,
  notificationLink,
  dueWhen,
  useNotificationList,
  useUnreadCount,
  type AppNotification,
} from './notifications';
import './notifications.css';

/** Bell with an unread badge and a dropdown list. Polls every 30 s and on window focus. */
export function NotificationBell({ size = 18 }: { size?: number }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const unread = useUnreadCount();
  const count = unread.data ?? 0;

  // A new notification arrived while the list is open or cached: refresh it.
  const last = useRef(count);
  useEffect(() => {
    if (count > last.current) void qc.invalidateQueries({ queryKey: listKey });
    last.current = count;
  }, [count, qc]);

  const label = count > 0 ? `${t('notifications.open')}: ${t('notifications.unread', { count })}` : t('notifications.open');
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Tip label={label} side="bottom">
        <Popover.Trigger asChild>
          <button className="icon-btn bell-btn" data-active={open} aria-label={label}>
            <BellIcon size={size} />
            <CountBadge count={count} tone="danger" />
          </button>
        </Popover.Trigger>
      </Tip>
      <Popover.Content className="popover notif-pop" align="end" sideOffset={8} collisionPadding={12} onOpenAutoFocus={(e) => e.preventDefault()} onCloseAutoFocus={(e) => e.preventDefault()}>
        <NotificationList onClose={() => setOpen(false)} />
      </Popover.Content>
    </Popover.Root>
  );
}

function NotificationList({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const list = useNotificationList(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const page = list.data;
  const unread = page?.unread ?? 0;

  const openItem = (n: AppNotification) => {
    track('notification_opened', { kind: n.kind, from: 'bell' });
    void markRead(qc, n);
    onClose();
    navigate(notificationLink(n));
  };

  return (
    <div className="notif">
      <div className="notif-head">
        <h3>{t('notifications.title')}</h3>
        {unread > 0 && (
          <button className="btn sm ghost" onClick={() => void markAllRead(qc)}>
            <CheckCheck size={15} /> {t('notifications.markAllRead')}
          </button>
        )}
      </div>
      <div className="notif-body">
        {!page && list.isLoading && (
          <div className="empty-state compact">
            <Spinner />
          </div>
        )}
        {!page && list.isError && <div className="empty-state compact">{errorText(list.error instanceof ApiError ? list.error.code : 'internal')}</div>}
        {page && page.items.length === 0 && (
          <div className="empty-state compact">
            <BellIcon size={22} />
            <strong>{t('notifications.empty')}</strong>
            <span>{t('notifications.emptyHint')}</span>
          </div>
        )}
        {page?.items.map((n) => <NotificationRow key={n.id} n={n} onOpen={() => openItem(n)} />)}
        {page?.next && (
          <button
            className="btn sm ghost notif-more"
            disabled={loadingMore}
            onClick={async () => {
              setLoadingMore(true);
              try {
                await loadMore(qc, page);
              } finally {
                setLoadingMore(false);
              }
            }}
          >
            {t('notifications.more')}
          </button>
        )}
      </div>
    </div>
  );
}

/** One notification: who, what, when; unread ones are marked. Shared by the bell and the inbox. */
export function NotificationRow({ n, onOpen }: { n: AppNotification; onOpen: () => void }) {
  return (
    <button className="notif-item" data-unread={!n.read} onClick={onOpen}>
      <span className="notif-avatar">
        {n.payload.actor ? (
          <Avatar name={n.payload.actor.name} color={n.payload.actor.color} size={32} />
        ) : (
          <span className="avatar notif-system" style={{ width: 32, height: 32 }}>
            <CalendarClock size={16} />
          </span>
        )}
        <span className="notif-kind">
          <KindIcon kind={n.kind} />
        </span>
      </span>
      <span className="notif-main">
        <NotificationText n={n} />
        {n.payload.text && <span className="notif-quote">{n.payload.text}</span>}
        <span className="notif-time">{relativeTime(n.createdAt)}</span>
      </span>
      {!n.read && <span className="notif-dot" aria-hidden="true" />}
    </button>
  );
}

function KindIcon({ kind }: { kind: string }) {
  if (kind === 'mention' || kind === 'reply') return <MessageCircle size={11} />;
  if (kind === 'board_shared' || kind === 'project_shared') return <Share2 size={11} />;
  if (kind === 'team_invite') return <Users size={11} />;
  if (kind === 'org_invite') return <Building2 size={11} />;
  if (kind === 'issue_assigned' || kind === 'issue_status') return <SquareKanban size={11} />;
  if (kind === 'issue_commented' || kind === 'issue_mention' || kind === 'page_mention' || kind === 'page_comment') return <MessageCircle size={11} />;
  if (kind === 'issue_due' || kind === 'page_review_due') return <CalendarClock size={11} />;
  if (kind.startsWith('page_review')) return <FileCheck size={11} />;
  if (kind === 'space_post') return <Newspaper size={11} />;
  return <BellIcon size={11} />;
}

const NAME = '\u0000';

/** "<b>Ann</b> mentioned you on “Board”": the name in bold, everything else plain text. */
function NotificationText({ n }: { n: AppNotification }) {
  const { t, i18n } = useTranslation();
  const p = n.payload;
  const key = n.kind === 'page_review_decided' && p.decision === 'changes' ? 'notifications.page_review_changes' : `notifications.${n.kind}`;
  if (!i18n.exists(key) || typeof t(key) !== 'string') return <span className="notif-text">{t('notifications.unknown')}</span>;
  const when = n.kind === 'issue_due' || n.kind === 'page_review_due' ? t(`notifications.due.${dueWhen(p.due, todayISO())}`) : '';
  const text = t(key, {
    name: NAME,
    board: p.boardTitle ?? '',
    space: p.spaceName ?? '',
    page: p.pageTitle ?? '',
    team: p.teamName ?? '',
    org: p.orgName ?? '',
    issue: p.key ?? '',
    title: p.title ?? '',
    status: p.status ?? '',
    when,
    project: p.projectKey ? `${p.projectKey} · ${p.projectName ?? ''}` : '',
    role: p.role ? t(`trackerSettings.members.roles.${p.role}`).toLowerCase() : '',
  });
  const [before, after = ''] = text.split(NAME);
  return (
    <span className="notif-text">
      {before}
      <b>{p.actor?.name ?? ''}</b>
      {after}
    </span>
  );
}
